import React from 'react';
import {
  employerLabels,
  filledBeneficiaries,
  fmtCommunicationFrequency,
  fmtCountry,
  fmtDate,
  fmtDelivery,
  fmtEmploymentStatus,
  fmtOccupation,
  fmtText,
  fmtYesNo,
  formatAddress,
  formatCents,
  hasEmployerDetails,
  householdHasSpouse,
  secondaryTcpHasData,
  sectionKey,
  sumCents,
} from './pdsFormat';

const Row = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 py-1 border-bottom">
    <span className="text-muted small">{label}</span>
    <span className="text-end small fw-semibold">{value}</span>
  </div>
);

const Notes = ({ value }) => {
  const text = String(value || '').trim();
  if (!text) return null;
  return (
    <div className="alert alert-warning py-2 px-3 mt-2 mb-0 small">
      <strong className="d-block mb-1">Client notes</strong>
      {text}
    </div>
  );
};

const SectionCard = ({ title, children }) => (
  <div className="card mb-3">
    <div className="card-header py-2"><strong>{title}</strong></div>
    <div className="card-body p-3">{children}</div>
  </div>
);

function BeneficiaryTable({ label, rows }) {
  const filled = filledBeneficiaries(rows);
  if (filled.length === 0) {
    return <Row label={`${label} beneficiaries`} value="None listed" />;
  }

  const total = sumCents(rows);
  const isExact = total === 10000;

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fw-semibold small">{label} beneficiaries</span>
        <span className={`badge ${isExact ? 'bg-success' : 'bg-danger'}`}>
          Total {formatCents(total)}%
        </span>
      </div>
      <div className="table-responsive">
        <table className="table table-sm mb-0 align-middle">
          <thead>
            <tr className="small text-muted">
              <th style={{ width: '8%' }}>%</th>
              <th style={{ width: '16%' }}>Relationship</th>
              <th style={{ width: '24%' }}>Full Name</th>
              <th style={{ width: '14%' }}>Date of Birth</th>
              <th>Mailing Address</th>
            </tr>
          </thead>
          <tbody>
            {filled.map((r, i) => (
              <tr key={i} className="small">
                <td>{fmtText(r.pct)}</td>
                <td>{fmtText(r.relationship)}</td>
                <td>{fmtText(r.full_name)}</td>
                <td>{fmtDate(r.dob)}</td>
                <td>{formatAddress(r.address)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!isExact && (
        <div className="text-danger small mt-1">
          <i className="bi bi-exclamation-triangle me-1" />
          These percentages do not total 100.00% — confirm with the client
          before submitting the account application.
        </div>
      )}
    </div>
  );
}

function PersonBlock({ heading, prefix, formData, spousePresent }) {
  const get = (suffix) => formData[sectionKey(prefix, suffix)] || {};

  const contact = get('contact');
  const employment = get('employment');
  const compliance = get('compliance');
  const tcp = get('trusted_contact');
  const bene = get('beneficiaries');
  const skipPrimary = spousePresent && bene.spouse_is_sole_primary === 'yes';

  return (
    <div className="mb-4">
      <h6 className="text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">
        {heading}
      </h6>

      <SectionCard title="Contact Information">
        <Row label="Full legal name" value={fmtText(contact.full_name)} />
        <Row label="Preferred name" value={fmtText(contact.preferred_name)} />
        <Row label="Known by another name" value={fmtYesNo(contact.known_by_other_name)} />
        {contact.known_by_other_name === 'yes' && (
          <Row label="Other name" value={fmtText(contact.other_name)} />
        )}
        <Row label="Date of birth" value={fmtDate(contact.date_of_birth)} />
        <Row label="Primary phone" value={fmtText(contact.phone)} />
        <Row label="Email" value={fmtText(contact.email)} />
        <Row label="Legal / home address" value={formatAddress(contact.address)} />
        <Row label="Mother's maiden name" value={fmtText(contact.mothers_maiden_name)} />
        <Row label="Security question" value={fmtText(contact.security_question)} />
        <Row label="Security answer" value={fmtText(contact.security_answer)} />
        <Row
          label="Country(ies) of citizenship"
          value={fmtCountry(contact.citizenship, contact.citizenship_other)}
        />
        <Row
          label="Country(ies) of legal residence"
          value={fmtCountry(contact.residence, contact.residence_other)}
        />
        <Row
          label="Country(ies) of birth"
          value={fmtCountry(contact.birth_country, contact.birth_country_other)}
        />
        <Notes value={contact.notes} />
      </SectionCard>

      <SectionCard title="Employment">
        <Row label="Employment status" value={fmtEmploymentStatus(employment.status)} />
        {hasEmployerDetails(employment) && (
          <>
            <Row
              label={employerLabels(employment).name}
              value={fmtText(employment.employer_name)}
            />
            <Row
              label={employerLabels(employment).address}
              value={formatAddress(employment.employer_address)}
            />
          </>
        )}
        {employment.occupation && (
          <Row label="Occupation" value={fmtOccupation(employment)} />
        )}
        <Notes value={employment.notes} />
      </SectionCard>

      <SectionCard title="Compliance & Reporting">
        <Row
          label="Affiliated with an exchange, FINRA, or a municipal securities broker-dealer"
          value={fmtYesNo(compliance.exchange_affiliated)}
        />
        {compliance.exchange_affiliated === 'yes' && (
          <Row label="Firm and relationship" value={fmtText(compliance.exchange_affiliated_details)} />
        )}
        <Row
          label="Director, 10% shareholder, or policy-making officer of a public company"
          value={fmtYesNo(compliance.public_company_insider)}
        />
        {compliance.public_company_insider === 'yes' && (
          <Row label="Company and ticker" value={fmtText(compliance.public_company_insider_details)} />
        )}
        <Row label="Paperless regulatory documents" value={fmtDelivery(compliance.paperless_delivery)} />
        <Row label="Quarterly reports via client portal" value={fmtDelivery(compliance.quarterly_reports_portal)} />
        <Row
          label="How often they want to hear from us"
          value={fmtCommunicationFrequency(compliance.communication_frequency)}
        />
        <Notes value={compliance.notes} />
      </SectionCard>

      <SectionCard title="Trusted Contact Person">
        <Row label="Full name" value={fmtText(tcp.full_name)} />
        <Row label="Relationship to client" value={fmtText(tcp.relationship)} />
        <Row label="Phone" value={fmtText(tcp.phone)} />
        <Row label="Email" value={fmtText(tcp.email)} />
        <Row label="Physical address" value={formatAddress(tcp.address)} />
        {secondaryTcpHasData(tcp) && (
          <>
            <div className="fw-semibold small mt-3 mb-1">Secondary Trusted Contact</div>
            <Row label="Full name" value={fmtText(tcp.secondary.full_name)} />
            <Row label="Relationship to client" value={fmtText(tcp.secondary.relationship)} />
            <Row label="Phone" value={fmtText(tcp.secondary.phone)} />
            <Row label="Email" value={fmtText(tcp.secondary.email)} />
            <Row label="Physical address" value={formatAddress(tcp.secondary.address)} />
          </>
        )}
        <Notes value={tcp.notes} />
      </SectionCard>

      <SectionCard title="Beneficiaries">
        {bene.same_as_primary === true && (
          <Row label="Beneficiaries" value="Copied from the client's own designations" />
        )}
        {spousePresent && (
          <Row
            label="Spouse/partner is the 100% primary beneficiary"
            value={fmtYesNo(bene.spouse_is_sole_primary)}
          />
        )}
        {!skipPrimary && <BeneficiaryTable label="Primary" rows={bene.primary} />}
        <BeneficiaryTable label="Contingent" rows={bene.contingent} />
        <Notes value={bene.notes} />
      </SectionCard>
    </div>
  );
}

/**
 * Read-only transcript of a submitted Personal Data Sheet.
 *
 * Labels are written out longhand rather than derived from the field keys:
 * these are compliance-sensitive questions and they must read back exactly
 * as the client saw them.
 */
export default function PdsFormView({ formData }) {
  if (!formData || Object.keys(formData).length === 0) {
    return <p className="text-muted fst-italic mb-0">No form data submitted yet.</p>;
  }

  const spousePresent = householdHasSpouse(formData);
  const household = formData.household || {};

  return (
    <div>
      <SectionCard title="Household">
        <Row label="Has a spouse or partner" value={fmtYesNo(household.has_spouse)} />
        {spousePresent && household.spouse_name && (
          <Row label="Spouse / partner name" value={fmtText(household.spouse_name)} />
        )}
        <Notes value={household.notes} />
      </SectionCard>

      <PersonBlock
        heading="Client"
        prefix=""
        formData={formData}
        spousePresent={spousePresent}
      />

      {spousePresent && (
        <PersonBlock
          heading="Spouse / Partner"
          prefix="spouse"
          formData={formData}
          spousePresent={spousePresent}
        />
      )}

      <p className="text-muted small fst-italic mb-0">
        Social Security Numbers are intentionally not collected on this form —
        gather them at the Getting Started meeting.
      </p>
    </div>
  );
}
